/**
 * iter86 (Item W) - Notification sound + vibration cue.
 *
 * Design constraints (founder brief):
 *   - Sound: short, quiet, non-jarring. No copyrighted files. Fail silently
 *     if browser rejects play() (autoplay policy).
 *   - Vibration: navigator.vibrate feature-detected, safe no-op elsewhere.
 *     Does NOT work on iOS Safari - do not fake it there.
 *   - Debounce: 3 messages in 2 seconds must NOT stack into 3 overlapping
 *     sounds. Single cue only within the debounce window.
 *   - First mount guard: no chime on initial page load - only when the
 *     unread count strictly increases from a previously-known value.
 *
 * Implementation notes:
 *   - Sound is SYNTHESIZED via Web Audio API (two short soft sine tones,
 *     C5 then E5, gain-ramped so no click). This guarantees zero copyright
 *     risk and works cross-browser with no asset loading. Approximate
 *     duration: 400ms total, peak gain 0.12 (quiet).
 *   - Uses a single lazy AudioContext and short-circuits when suspended
 *     (some browsers suspend the context until a real user gesture).
 */
const DEBOUNCE_MS = 2000;
let _lastPlayedAt = 0;
let _ctx = null;

function _getCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    _ctx = new Ctor();
  } catch { _ctx = null; }
  return _ctx;
}

/**
 * Play the short cue. Fails silently on any error (autoplay rejection,
 * suspended context, missing Web Audio API, etc). Debounced.
 */
export function playNotificationCue() {
  const now = Date.now();
  if (now - _lastPlayedAt < DEBOUNCE_MS) return;   // debounce
  _lastPlayedAt = now;

  const ctx = _getCtx();
  if (!ctx) return;
  // Some browsers suspend the context - best-effort resume, ignore errors.
  try {
    if (ctx.state === "suspended") { ctx.resume().catch(() => {}); }
  } catch {}

  try {
    const t0 = ctx.currentTime;
    const notes = [
      { freq: 523.25, start: 0.00, dur: 0.16 },   // C5
      { freq: 659.25, start: 0.14, dur: 0.22 },   // E5
    ];
    const master = ctx.createGain();
    master.gain.value = 0.12;                      // quiet by default
    master.connect(ctx.destination);

    for (const n of notes) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = n.freq;
      const g = ctx.createGain();
      // Envelope: gentle attack + decay so there's no click.
      g.gain.setValueAtTime(0.0001, t0 + n.start);
      g.gain.exponentialRampToValueAtTime(1.0, t0 + n.start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.start + n.dur);
      osc.connect(g);
      g.connect(master);
      osc.start(t0 + n.start);
      osc.stop(t0 + n.start + n.dur + 0.02);
    }
  } catch {
    // Web Audio failure - fail silently per the founder brief.
  }
}

/**
 * Vibrate the device (Android Chrome, some other browsers). Safe no-op
 * where unsupported (iOS Safari has never implemented navigator.vibrate).
 * We do NOT try to fake a vibration on unsupported platforms - the UI must
 * not imply the feature is broken when it's simply a platform limitation.
 */
export function safeVibrate(pattern = 40) {
  try {
    if (typeof navigator === "undefined") return { supported: false };
    if (typeof navigator.vibrate !== "function") return { supported: false };
    navigator.vibrate(pattern);
    return { supported: true };
  } catch {
    return { supported: false };
  }
}
